import { useEffect, useRef } from 'react';
import { useQueryClient } from '@zg-rentals/react-query5';
import { useAreaByResourceId } from '@/src/lib/hooks/query/useAreaQuery';
import { boundaryKeys } from '@/src/lib/hooks/query/useBoundaryQuery';
import { useCurrentArea, useSetCurrentArea } from '@/src/lib/hooks/query/useCurrentArea';
import type { AreaResponse } from '@/src/lib/api';

type UseSrpAreaDataParams = {
  resourceId: string;
  isPdpPage: boolean;
};

type UseSrpAreaDataResult = {
  area: AreaResponse | undefined;
  isLoading: boolean;
  areaError: Error | null;
  refetchArea: () => void;
};

/**
 * Manages area data for the SRP page: fetches by resourceId for SRP,
 * reads from cache for PDP, syncs to global current area, and cleans up
 * stale boundary data when the area changes.
 */
export function useSrpAreaData({ resourceId, isPdpPage }: UseSrpAreaDataParams): UseSrpAreaDataResult {
  const setCurrentArea = useSetCurrentArea();
  const queryClient = useQueryClient();

  const {
    data: srpArea,
    isLoading: isSrpAreaLoading,
    error: areaError,
    refetch: refetchArea,
  } = useAreaByResourceId(resourceId, {
    enabled: !isPdpPage && !!resourceId,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  const { data: hdpArea } = useCurrentArea();

  const area = isPdpPage ? hdpArea : srpArea;
  const isLoading = isPdpPage ? false : isSrpAreaLoading;

  // Remove stale boundary data when area ID changes
  const previousAreaIdRef = useRef<string | undefined>();
  useEffect(() => {
    const currentAreaId = area?.id;

    if (previousAreaIdRef.current && previousAreaIdRef.current !== currentAreaId) {
      queryClient.removeQueries({ queryKey: boundaryKeys.byId(previousAreaIdRef.current) });
    }

    previousAreaIdRef.current = currentAreaId;
  }, [area?.id, queryClient]);

  // Update the global "current area" when this page's area data loads
  useEffect(() => {
    if (area) {
      setCurrentArea(area);
    }
  }, [area, setCurrentArea]);

  return {
    area,
    isLoading,
    areaError: areaError as Error | null,
    refetchArea,
  };
}
